/**
 * NewHandshakeDialog v2 (D31 slice 3) — FULL REPLACEMENT for
 * src/components/network/NewHandshakeDialog.tsx.
 *
 * When the chosen connection is an on-Eaze vendor and you're the distributor,
 * the dialog fetches GET /connections/:id/catalog and becomes a REORDER
 * surface: pick products from the supplier's live catalog — real-time
 * availability shown, distributor-tier price prefilled (editable, pending
 * counter-offers), and the vendor's productId stamped on the line so the
 * close consumes by exact ID. External vendors (or the vendor role) keep the
 * manual free-text lines exactly as before.
 */
import { useCurrency } from "@/lib/currency";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2, PackageSearch } from "lucide-react";
import { toast } from "sonner";
import api from "@/services/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConnections } from "@/hooks/useConnections";
import { useCreateHandshake } from "@/hooks/useHandshakes";
import { fmtMoney } from "./helpers";

interface CatalogItem {
  productId: string;
  name: string;
  sku: string | null;
  available: number;
  distributorPrice: number;
  unitPriceMinor: number;
}

interface DraftLine {
  productId?: string | null;
  productName: string;
  vendorSku?: string | null;
  quantity: string;
  unitPrice: string; // what people type, in the business's money (major units)
  note: string;
  available?: number | null;
}

const EMPTY_LINE: DraftLine = {
  productName: "",
  quantity: "1",
  unitPrice: "0",
  note: "",
};

const toMinorUnits = (v: string) => Math.round((parseFloat(v) || 0) * 100);

export function NewHandshakeDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { data: connections } = useConnections("active");
  const cur = useCurrency();
  const [connectionId, setConnectionId] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [manualLines, setManualLines] = useState<DraftLine[]>([
    { ...EMPTY_LINE },
  ]);
  const [catalogPick, setCatalogPick] = useState("");
  const [notes, setNotes] = useState("");
  const [expectedDeliveryAt, setExpectedDeliveryAt] = useState("");

  const create = useCreateHandshake();

  const selectedConn = useMemo(
    () => (connections || []).find((c) => c._id === connectionId) || null,
    [connections, connectionId],
  );
  const catalogMode =
    !!selectedConn &&
    selectedConn.vendorOnEaze &&
    selectedConn.myRole === "distributor";

  const { data: catalogData, isLoading: catalogLoading } = useQuery({
    queryKey: ["connection-catalog", connectionId],
    queryFn: async () =>
      (await api.get(`/connections/${connectionId}/catalog`)).data as {
        vendorName: string;
        catalog: CatalogItem[];
      },
    enabled: open && catalogMode,
  });
  const catalog = catalogData?.catalog ?? [];
  const catalogById = useMemo(
    () => new Map(catalog.map((c) => [c.productId, c])),
    [catalog],
  );

  const activeLines = catalogMode ? lines : manualLines;

  const reset = () => {
    setConnectionId("");
    setLines([]);
    setManualLines([{ ...EMPTY_LINE }]);
    setCatalogPick("");
    setNotes("");
    setExpectedDeliveryAt("");
  };
  const handleClose = () => {
    reset();
    onClose();
  };

  const addFromCatalog = (productId: string) => {
    if (!productId || lines.some((l) => l.productId === productId)) return;
    const item = catalogById.get(productId);
    if (!item) return;
    setLines((prev) => [
      ...prev,
      {
        productId: item.productId,
        productName: item.name,
        vendorSku: item.sku,
        quantity: "1",
        unitPrice: String((item.unitPriceMinor || 0) / 100),
        note: "",
        available: item.available,
      },
    ]);
    setCatalogPick("");
  };

  const updateLine = (idx: number, patch: Partial<DraftLine>) => {
    const setter = catalogMode ? setLines : setManualLines;
    setter((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const removeLine = (idx: number) => {
    const setter = catalogMode ? setLines : setManualLines;
    setter((prev) => prev.filter((_, i) => i !== idx));
  };

  const grandTotalMinor = useMemo(
    () =>
      activeLines.reduce((sum, l) => {
        const q = parseInt(l.quantity, 10) || 0;
        const p = toMinorUnits(l.unitPrice);
        return sum + q * p;
      }, 0),
    [activeLines],
  );

  const canSubmit =
    !!connectionId &&
    activeLines.length > 0 &&
    activeLines.every(
      (l) => l.productName.trim() && (parseInt(l.quantity, 10) || 0) >= 1,
    );

  const handleSubmit = () => {
    create.mutate(
      {
        connectionId,
        lines: activeLines.map((l) => ({
          productId: l.productId || undefined,
          productName: l.productName.trim(),
          vendorSku: l.vendorSku || undefined,
          quantity: parseInt(l.quantity, 10),
          unitPriceMinor: toMinorUnits(l.unitPrice),
          note: l.note.trim() || undefined,
        })),
        notes: notes.trim() || undefined,
        expectedDeliveryAt: expectedDeliveryAt || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Reorder request sent");
          handleClose();
        },
        onError: (e: any) =>
          toast.error(e?.response?.data?.message || "Failed"),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New handshake</DialogTitle>
          <DialogDescription>
            {catalogMode
              ? `Reorder from ${catalogData?.vendorName || "your supplier"}'s live catalog — availability and your prices, in real time.`
              : "Request goods from a vendor or send to a distributor."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Connection *</Label>
            <Select
              value={connectionId}
              onValueChange={(v) => {
                setConnectionId(v);
                setLines([]);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an active connection" />
              </SelectTrigger>
              <SelectContent>
                {(connections || []).map((c) => {
                  const otherSide =
                    c.myRole === "distributor" ? c.vendor : c.distributor;
                  return (
                    <SelectItem key={c._id} value={c._id}>
                      {otherSide.displayName} ({c.myRole}
                      {c.vendorOnEaze ? " · on Eaze" : " · external"})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* ── Catalog reorder mode ── */}
          {catalogMode && (
            <div className="space-y-2">
              <Label>Add from catalog</Label>
              <Select value={catalogPick} onValueChange={addFromCatalog}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      catalogLoading ? "Loading catalog…" : "Pick a product…"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {catalog.map((item) => (
                    <SelectItem key={item.productId} value={item.productId}>
                      {item.name} — {fmtMoney(item.unitPriceMinor)} ·{" "}
                      {item.available} in stock
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!catalogLoading && catalog.length === 0 && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <PackageSearch className="h-3.5 w-3.5" /> Supplier has no
                  products visible yet.
                </p>
              )}
            </div>
          )}

          {/* ── Manual mode (external vendor / vendor role) ── */}
          {!catalogMode && connectionId && (
            <div className="flex items-center justify-between">
              <Label>Line items *</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setManualLines((p) => [...p, { ...EMPTY_LINE }])}
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add line
              </Button>
            </div>
          )}

          {/* ── Lines table (both modes) ── */}
          {activeLines.length > 0 && (
            <div className="space-y-2">
              {activeLines.map((line, idx) => {
                const q = parseInt(line.quantity, 10) || 0;
                const overAvail = line.available != null && q > line.available;
                return (
                  <div
                    key={line.productId || idx}
                    className="grid grid-cols-12 gap-2 p-2 border rounded-md items-center"
                  >
                    {catalogMode ? (
                      <div className="col-span-5 min-w-0">
                        <div className="truncate text-sm font-medium">
                          {line.productName}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {line.available} in stock
                          {line.vendorSku ? ` · ${line.vendorSku}` : ""}
                        </div>
                      </div>
                    ) : (
                      <Input
                        className="col-span-5"
                        placeholder="Product name"
                        value={line.productName}
                        onChange={(e) =>
                          updateLine(idx, { productName: e.target.value })
                        }
                      />
                    )}
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min={1}
                        placeholder="Qty"
                        value={line.quantity}
                        onChange={(e) =>
                          updateLine(idx, { quantity: e.target.value })
                        }
                        className={overAvail ? "border-amber-400" : undefined}
                      />
                      {overAvail && (
                        <p className="text-[10px] text-amber-600 mt-0.5">
                          exceeds stock
                        </p>
                      )}
                    </div>
                    <Input
                      className="col-span-4"
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder={`Unit price (${cur.symbol})`}
                      aria-label={`Unit price (${cur.symbol})`}
                      value={line.unitPrice}
                      onChange={(e) =>
                        updateLine(idx, { unitPrice: e.target.value })
                      }
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="col-span-1"
                      onClick={() => removeLine(idx)}
                      disabled={!catalogMode && activeLines.length === 1}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
              <div className="text-right text-sm">
                Grand total:{" "}
                <strong className="font-medium">
                  {fmtMoney(grandTotalMinor)}
                </strong>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Expected delivery (optional)</Label>
              <Input
                type="date"
                value={expectedDeliveryAt}
                onChange={(e) => setExpectedDeliveryAt(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={1}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || create.isPending}
          >
            {create.isPending
              ? "Sending…"
              : catalogMode
                ? "Send reorder"
                : "Create handshake"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
